import React, { useState } from 'react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';

/**
 * Inline horse picker for use inside Quick Log modals on the global page.
 * mode: 'multi' (default) — checkboxes; 'single' — radio.
 */
export default function HorsePickerInline({ availableHorses, value, onChange, mode = 'multi', locations }) {
  const [q, setQ] = useState('');
  const [locFilter, setLocFilter] = useState('');
  if (!Array.isArray(availableHorses)) return null;
  const filtered = availableHorses.filter(h => {
    if (q && !(h.system_name || h.name || '').toLowerCase().includes(q.toLowerCase())) return false;
    if (locFilter && h.farmfolio_location_id !== locFilter) return false;
    return true;
  });
  const ids = mode === 'multi' ? (value || []) : (value ? [value] : []);
  const toggle = (id) => {
    if (mode === 'single') { onChange(id); return; }
    onChange(ids.includes(id) ? ids.filter(x => x !== id) : [...ids, id]);
  };
  return (
    <div className="space-y-1">
      <Label className="font-semibold">{mode === 'single' ? 'Horse' : 'Horses'}</Label>
      <div className="border rounded p-2 space-y-2">
        <div className="flex gap-1 items-center">
          <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search…"
            className="h-7 text-xs flex-1" data-testid="picker-horse-search" />
          {locations?.length > 0 && (
            <select
              className="h-7 text-[10px] border rounded px-1.5 bg-white text-gray-700 shrink-0"
              value={locFilter}
              onChange={e => setLocFilter(e.target.value)}
            >
              <option value="">All Locations</option>
              {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
            </select>
          )}
        </div>
        {mode === 'multi' && (() => {
          const filt = filtered;
          const BUCKETS = [
            { k: 'all',       label: 'All',        fn: () => filt.map(h => h.id) },
            { k: 'colts',     label: 'Colts',      fn: () => filt.filter(h => h.gender === 'Colt').map(h => h.id) },
            { k: 'fillies',   label: 'Fillies',    fn: () => filt.filter(h => h.gender === 'Filly').map(h => h.id) },
            { k: 'mares',     label: 'Mares',      fn: () => filt.filter(h => h.gender === 'Mare').map(h => h.id) },
            { k: 'horses',    label: 'Horses',     fn: () => filt.filter(h => ['Horse','Stallion','Gelding','Ridgeling'].includes(h.gender)).map(h => h.id) },
            { k: 'weanlings', label: 'Weanlings',  fn: () => filt.filter(h => h.age === 'Weanling').map(h => h.id) },
            { k: 'yearlings', label: 'Yearlings',  fn: () => filt.filter(h => h.age === 'Yearling').map(h => h.id) },
            { k: 'layup',     label: 'On Layup',   fn: () => filt.filter(h => h.on_layup).map(h => h.id) },
          ];
          return (
            <div className="flex flex-wrap gap-1">
              {BUCKETS.map(b => (
                <button key={b.k} type="button" onClick={() => onChange([...new Set([...(value || []), ...b.fn()])])}
                  data-testid={`picker-bucket-${b.k}`}
                  className="px-2 py-0.5 rounded-full text-[10px] font-medium border bg-white text-gray-600 border-gray-200 hover:bg-gray-50">
                  + {b.label}
                </button>
              ))}
              <button type="button" onClick={() => onChange([])}
                className="px-2 py-0.5 rounded-full text-[10px] font-medium border bg-white hover:bg-gray-50">Clear</button>
            </div>
          );
        })()}
        <div className="max-h-32 overflow-y-auto space-y-1">
          {filtered.map(h => (
            <label key={h.id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-muted/30 px-1 rounded">
              <input type={mode === 'single' ? 'radio' : 'checkbox'}
                checked={ids.includes(h.id)} onChange={() => toggle(h.id)}
                data-testid={`picker-horse-${h.id}`} />
              <span className="flex-1 truncate">{h.system_name || h.name}</span>
              <span className="text-[10px] text-muted-foreground">{h.gender} · {h.age}</span>
            </label>
          ))}
        </div>
        <p className="text-[10px] text-muted-foreground">
          {mode === 'multi' ? `${ids.length} horse(s) selected` : (ids[0] ? '1 selected' : 'None selected')}
        </p>
      </div>
    </div>
  );
}
